import toast from 'react-hot-toast';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import AdminStreamingServices from '../components/AdminStreamingServices';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, Users, TrendingUp, Eye, Star, Film, BarChart3, Database, Clock, Play, Mail, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { usePageView } from '../hooks/usePageView';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = ({ user, setUser }) => {
  usePageView('admin');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [growthData, setGrowthData] = useState([]);
  const [growthPeriod, setGrowthPeriod] = useState(30);
  const [trafficData, setTrafficData] = useState([]);
  const [trafficPeriod, setTrafficPeriod] = useState(30);
  const [trafficPage, setTrafficPage] = useState('all');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [indexStatus, setIndexStatus] = useState(null);
  const [creatingIndexes, setCreatingIndexes] = useState(false);
  const [schedulerSettings, setSchedulerSettings] = useState(null);
  const [pendingDeletions, setPendingDeletions] = useState(null);
  const [updatingScheduler, setUpdatingScheduler] = useState(false);
  const [runningDeletionNow, setRunningDeletionNow] = useState(false);
  const [synopsisStats, setSynopsisStats] = useState(null);
  const [backfillingSync, setBackfillingSync] = useState(false);

  // Track window resize for mobile detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Aggregate data for mobile display
  const getAggregatedData = () => {
    if (!isMobile || growthPeriod === 7) {
      return growthData;
    }

    if (growthPeriod === 30) {
      // Aggregate into weekly totals (4-5 weeks)
      const weeks = [];
      for (let i = 0; i < growthData.length; i += 7) {
        const weekData = growthData.slice(i, i + 7);
        const weekTotal = weekData.reduce((sum, day) => sum + day.count, 0);
        const startDate = weekData[0]?.date || '';
        const endDate = weekData[weekData.length - 1]?.date || '';
        weeks.push({
          date: `${startDate.substring(5)}`,
          label: `${startDate.substring(5)} - ${endDate.substring(5)}`,
          count: weekTotal
        });
      }
      return weeks;
    }

    if (growthPeriod === 90) {
      // Aggregate into monthly totals (3 months)
      const months = {};
      growthData.forEach(day => {
        const monthKey = day.date.substring(0, 7); // YYYY-MM
        if (!months[monthKey]) {
          months[monthKey] = { date: monthKey, count: 0 };
        }
        months[monthKey].count += day.count;
      });
      return Object.values(months).map(m => ({
        ...m,
        label: new Date(m.date + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
      }));
    }

    return growthData;
  };

  const displayData = getAggregatedData();

  useEffect(() => {
    loadStats();
    loadGrowthData(growthPeriod);
    loadTrafficData(trafficPeriod, trafficPage);
    loadSynopsisStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have admin access');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadSynopsisStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/synopsis-stats`, { withCredentials: true });
      setSynopsisStats(response.data);
    } catch (error) {
      console.error('Error loading synopsis stats:', error);
    }
  };

  const runSynopsisBackfill = async () => {
    setBackfillingSync(true);
    try {
      const response = await axios.post(`${API}/admin/backfill-synopses`, {}, { withCredentials: true });
      if (response.data.success) {
        toast.success(`Backfill complete: ${response.data.updated} shows updated`);
        loadSynopsisStats(); // Refresh stats
      } else {
        toast.error(response.data.error || 'Backfill failed');
      }
    } catch (error) {
      console.error('Error running backfill:', error);
      toast.error('Failed to run synopsis backfill');
    } finally {
      setBackfillingSync(false);
    }
  };

  const loadGrowthData = async (days) => {
    try {
      const response = await axios.get(`${API}/admin/user-growth?days=${days}`, { withCredentials: true });
      setGrowthData(response.data);
    } catch (error) {
      console.error('Error loading growth data:', error);
    }
  };

  const handlePeriodChange = (days) => {
    setGrowthPeriod(days);
    loadGrowthData(days);
  };

  const loadTrafficData = async (days, page) => {
    try {
      const pageParam = page === 'all' ? '' : `&page=${page}`;
      const response = await axios.get(`${API}/admin/traffic-growth?days=${days}${pageParam}`, { withCredentials: true });
      setTrafficData(response.data);
    } catch (error) {
      console.error('Error loading traffic data:', error);
    }
  };

  const handleTrafficPeriodChange = (days) => {
    setTrafficPeriod(days);
    loadTrafficData(days, trafficPage);
  };

  const handleTrafficPageChange = (page) => {
    setTrafficPage(page);
    loadTrafficData(trafficPeriod, page);
  };

  // Aggregate traffic data for mobile display
  const getAggregatedTrafficData = () => {
    if (!isMobile || trafficPeriod === 7) {
      return trafficData;
    }

    if (trafficPeriod === 30) {
      const weeks = [];
      for (let i = 0; i < trafficData.length; i += 7) {
        const weekData = trafficData.slice(i, i + 7);
        const humansTotal = weekData.reduce((sum, day) => sum + (day.humans || 0), 0);
        const botsTotal = weekData.reduce((sum, day) => sum + (day.bots || 0), 0);
        const startDate = weekData[0]?.date || '';
        weeks.push({
          date: startDate,
          label: startDate.substring(5),
          humans: humansTotal,
          bots: botsTotal
        });
      }
      return weeks;
    }

    if (trafficPeriod === 90) {
      const months = {};
      trafficData.forEach(day => {
        const monthKey = day.date.substring(0, 7);
        if (!months[monthKey]) {
          months[monthKey] = { date: monthKey, humans: 0, bots: 0 };
        }
        months[monthKey].humans += day.humans || 0;
        months[monthKey].bots += day.bots || 0;
      });
      return Object.values(months).map(m => ({
        ...m,
        label: new Date(m.date + '-01').toLocaleDateString('en-US', { month: 'short' })
      }));
    }

    return trafficData;
  };

  const displayTrafficData = getAggregatedTrafficData();

  const loadIndexStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/index-status`, { withCredentials: true });
      setIndexStatus(response.data);
    } catch (error) {
      console.error('Error loading index status:', error);
    }
  };

  const createDatabaseIndexes = async () => {
    setCreatingIndexes(true);
    try {
      const response = await axios.post(`${API}/admin/create-indexes`, {}, { withCredentials: true });
      const result = response.data;
      
      toast.success(`Indexes created! ${result.summary.created} new, ${result.summary.skipped} already existed.`);
      
      // Reload status to show updated state
      await loadIndexStatus();
    } catch (error) {
      console.error('Error creating indexes:', error);
      toast.error('Failed to create indexes. Please try again.');
    } finally {
      setCreatingIndexes(false);
    }
  };

  const loadSchedulerSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/scheduler/settings`, { withCredentials: true });
      setSchedulerSettings(response.data);
    } catch (error) {
      console.error('Error loading scheduler settings:', error);
    }
  };

  const loadPendingDeletions = async () => {
    try {
      const response = await axios.get(`${API}/admin/scheduler/pending-deletions`, { withCredentials: true });
      setPendingDeletions(response.data);
    } catch (error) {
      console.error('Error loading pending deletions:', error);
    }
  };

  const updateSchedulerInterval = async (newInterval) => {
    setUpdatingScheduler(true);
    try {
      const response = await axios.put(`${API}/admin/scheduler/settings`, {
        interval_days: newInterval,
        enabled: schedulerSettings?.enabled ?? true
      }, { withCredentials: true });
      setSchedulerSettings(response.data);
      toast.success(`Scheduler updated to run every ${newInterval} days`);
    } catch (error) {
      console.error('Error updating scheduler:', error);
      toast.error('Failed to update scheduler');
    } finally {
      setUpdatingScheduler(false);
    }
  };

  const toggleScheduler = async () => {
    setUpdatingScheduler(true);
    try {
      const newEnabled = !schedulerSettings?.enabled;
      const response = await axios.put(`${API}/admin/scheduler/settings`, {
        interval_days: schedulerSettings?.interval_days ?? 30,
        enabled: newEnabled
      }, { withCredentials: true });
      setSchedulerSettings(response.data);
      toast.success(`Scheduler ${newEnabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error toggling scheduler:', error);
      toast.error('Failed to toggle scheduler');
    } finally {
      setUpdatingScheduler(false);
    }
  };

  const runDeletionProcessNow = async () => {
    setRunningDeletionNow(true);
    try {
      await axios.post(`${API}/admin/scheduler/run-now`, {}, { withCredentials: true });
      toast.success('Deletion process completed');
      await loadPendingDeletions();
    } catch (error) {
      console.error('Error running deletion process:', error);
      toast.error('Failed to run deletion process');
    } finally {
      setRunningDeletionNow(false);
    }
  };

  // Load index status and scheduler settings on mount
  useEffect(() => {
    loadIndexStatus();
    loadSchedulerSettings();
    loadPendingDeletions();
  }, []);

  if (loading) {
    return (
      <div className="admin-page">
        <Navigation user={user} setUser={setUser} />
        <div className="loading-container">
          <Loader2 className="spinner" size={48} />
          <p>Loading admin dashboard...</p>
        </div>
        <style jsx>{`
          .admin-page {
            min-height: 100vh;
            background: #121212;
          }
          .loading-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 60vh;
            color: #ffffff;
            gap: 1rem;
          }
          .spinner {
            animation: spin 1s linear infinite;
            color: #E62F88;
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="admin-page">
        <Navigation user={user} setUser={setUser} />
        <div className="error-container">
          <p>Failed to load admin stats. You may not have admin access.</p>
        </div>
      </div>
    );
  }

  const adsenseReady = stats.page_views.this_month >= 300 && stats.users.total >= 10;

  return (
    <div className="admin-page">
      <Navigation user={user} setUser={setUser} />

      <div className="admin-content">
        <div className="page-header">
          <h1>Admin Dashboard</h1>
          <p>Analytics and statistics for Baad.tv</p>
        </div>

        {/* Key Metrics */}
        <div className="metrics-grid">
          <Card className="metric-card">
            <CardHeader>
              <CardTitle className="metric-title">
                <Users size={24} />
                Total Users
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="metric-value">{stats.users.total}</div>
              <div className="metric-details">
                <span>{stats.users.today} today</span>
                <span>{stats.users.this_week} this week</span>
                <span>{stats.users.this_month} this month</span>
              </div>
            </CardContent>
          </Card>

          <Card className="metric-card">
            <CardHeader>
              <CardTitle className="metric-title">
                <TrendingUp size={24} />
                Active Users
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="metric-value">{stats.users.active}</div>
              <div className="metric-subtitle">Last 7 days</div>
            </CardContent>
          </Card>

          <Card className="metric-card">
            <CardHeader>
              <CardTitle className="metric-title">
                <Eye size={24} />
                Page Views
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="metric-value">{stats.page_views.total}</div>
              <div className="metric-details">
                <span>{stats.page_views.today} today</span>
                <span>{stats.page_views.this_week} this week</span>
                <span>{stats.page_views.this_month} this month</span>
              </div>
            </CardContent>
          </Card>

          <Card className="metric-card">
            <CardHeader>
              <CardTitle className="metric-title">
                <Film size={24} />
                Content
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="metric-value">{stats.content.total_shows}</div>
              <div className="metric-subtitle">Shows tracked</div>
              <div className="metric-secondary">{stats.content.total_reviews} reviews</div>
            </CardContent>
          </Card>
        </div>

        {/* AdSense Readiness */}
        <Card className={`adsense-card ${adsenseReady ? 'ready' : 'not-ready'}`}>
          <CardHeader>
            <CardTitle className="adsense-title">
              <BarChart3 size={24} />
              AdSense Readiness
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="adsense-status">
              <div className="status-icon">{adsenseReady ? '✅' : '⏳'}</div>
              <div className="status-text">
                <h3>{adsenseReady ? 'Ready for AdSense!' : 'Building Momentum'}</h3>
                <p>
                  {adsenseReady 
                    ? 'Your app meets basic requirements for AdSense application.' 
                    : 'Keep growing! AdSense typically requires consistent traffic and quality content.'}
                </p>
              </div>
            </div>
            <div className="adsense-requirements">
              <div className="requirement">
                <span className={stats.page_views.this_month >= 300 ? 'met' : 'unmet'}>
                  {stats.page_views.this_month >= 300 ? '✓' : '○'}
                </span>
                <span>300+ page views/month ({stats.page_views.this_month} current)</span>
              </div>
              <div className="requirement">
                <span className={stats.users.total >= 10 ? 'met' : 'unmet'}>
                  {stats.users.total >= 10 ? '✓' : '○'}
                </span>
                <span>10+ registered users ({stats.users.total} current)</span>
              </div>
              <div className="requirement">
                <span className={stats.content.total_reviews >= 20 ? 'met' : 'unmet'}>
                  {stats.content.total_reviews >= 20 ? '✓' : '○'}
                </span>
                <span>Quality content ({stats.content.total_reviews} reviews)</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* User Growth Chart */}
        <Card className="chart-card">
          <CardHeader>
            <CardTitle>User Growth</CardTitle>
            <div className="period-selector">
              <button 
                className={growthPeriod === 7 ? 'active' : ''} 
                onClick={() => handlePeriodChange(7)}
              >
                7 days
              </button>
              <button 
                className={growthPeriod === 30 ? 'active' : ''} 
                onClick={() => handlePeriodChange(30)}
              >
                30 days
              </button>
              <button 
                className={growthPeriod === 90 ? 'active' : ''} 
                onClick={() => handlePeriodChange(90)}
              >
                90 days
              </button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="simple-chart">
              {displayData.map((day, index) => {
                const maxCount = Math.max(...displayData.map(d => d.count), 1);
                const showLabel = isMobile || index % Math.ceil(displayData.length / 10) === 0;
                return (
                  <div key={day.date} className="chart-bar-container" title={day.label || day.date}>
                    <div 
                      className="chart-bar" 
                      style={{ 
                        height: `${(day.count / maxCount * 100)}%` 
                      }}
                    >
                      <span className="bar-value">{day.count > 0 ? day.count : ''}</span>
                    </div>
                    {showLabel && (
                      <div className="chart-label">
                        {isMobile && growthPeriod === 90 
                          ? day.label 
                          : day.date.substring(5)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {isMobile && growthPeriod > 7 && (
              <div className="chart-note">
                {growthPeriod === 30 ? 'Showing weekly totals' : 'Showing monthly totals'}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Page Views Breakdown */}
        <Card className="breakdown-card">
          <CardHeader>
            <CardTitle>Page Views by Page</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="breakdown-list">
              {Object.entries(stats.page_views.by_page).map(([page, count]) => (
                <div key={page} className="breakdown-item">
                  <div className="breakdown-bar-container">
                    <div className="breakdown-label">{page}</div>
                    <div className="breakdown-bar-wrapper">
                      <div 
                        className="breakdown-bar" 
                        style={{ 
                          width: `${(count / Math.max(...Object.values(stats.page_views.by_page))) * 100}%` 
                        }}
                      />
                    </div>
                    <div className="breakdown-count">{count}</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Bot vs Human Traffic */}
        <Card className="breakdown-card">
          <CardHeader>
            <CardTitle>🤖 Bot vs 👤 Human Traffic</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="traffic-stats">
              <div className="traffic-row">
                <span className="traffic-label">All Pages (Total)</span>
                <div className="traffic-values">
                  <span className="human-count">👤 {stats.page_views.humans || 0} humans</span>
                  <span className="bot-count">🤖 {stats.page_views.bots || 0} bots</span>
                </div>
              </div>
              {stats.page_views.landing && (
                <div className="traffic-row landing-row">
                  <span className="traffic-label">Landing Page (Total)</span>
                  <div className="traffic-values">
                    <span className="human-count">👤 {stats.page_views.landing.humans || 0} humans</span>
                    <span className="bot-count">🤖 {stats.page_views.landing.bots || 0} bots</span>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Traffic Growth Chart */}
        <Card className="chart-card">
          <CardHeader>
            <CardTitle>Traffic Over Time</CardTitle>
            <div className="chart-controls">
              <div className="period-selector">
                <button 
                  className={trafficPeriod === 7 ? 'active' : ''} 
                  onClick={() => handleTrafficPeriodChange(7)}
                >
                  7 days
                </button>
                <button 
                  className={trafficPeriod === 30 ? 'active' : ''} 
                  onClick={() => handleTrafficPeriodChange(30)}
                >
                  30 days
                </button>
                <button 
                  className={trafficPeriod === 90 ? 'active' : ''} 
                  onClick={() => handleTrafficPeriodChange(90)}
                >
                  90 days
                </button>
              </div>
              <div className="page-selector">
                <button 
                  className={trafficPage === 'all' ? 'active' : ''} 
                  onClick={() => handleTrafficPageChange('all')}
                >
                  All Pages
                </button>
                <button 
                  className={trafficPage === 'landing' ? 'active' : ''} 
                  onClick={() => handleTrafficPageChange('landing')}
                >
                  Landing
                </button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="traffic-legend">
              <span className="legend-item human">👤 Humans</span>
              <span className="legend-item bot">🤖 Bots</span>
            </div>
            <div className="stacked-chart">
              {displayTrafficData.map((day, index) => {
                const total = (day.humans || 0) + (day.bots || 0);
                const maxTotal = Math.max(...displayTrafficData.map(d => (d.humans || 0) + (d.bots || 0)), 1);
                const humanHeight = total > 0 ? ((day.humans || 0) / maxTotal * 100) : 0;
                const botHeight = total > 0 ? ((day.bots || 0) / maxTotal * 100) : 0;
                const showLabel = isMobile || index % Math.ceil(displayTrafficData.length / 10) === 0;
                return (
                  <div key={day.date} className="stacked-bar-container" title={`${day.label || day.date}: ${day.humans || 0} humans, ${day.bots || 0} bots`}>
                    <div className="stacked-bar">
                      {botHeight > 0 && (
                        <div className="bar-segment bot" style={{ height: `${botHeight}%` }}>
                          {day.bots > 0 && botHeight > 15 && <span className="segment-value">{day.bots}</span>}
                        </div>
                      )}
                      {humanHeight > 0 && (
                        <div className="bar-segment human" style={{ height: `${humanHeight}%` }}>
                          {day.humans > 0 && humanHeight > 15 && <span className="segment-value">{day.humans}</span>}
                        </div>
                      )}
                    </div>
                    {showLabel && (
                      <div className="chart-label">
                        {day.label || day.date.substring(5)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {isMobile && trafficPeriod > 7 && (
              <div className="chart-note">
                {trafficPeriod === 30 ? 'Showing weekly totals' : 'Showing monthly totals'}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Most Reviewed Shows */}
        {stats.content.most_reviewed_shows.length > 0 && (
          <Card className="top-shows-card">
            <CardHeader>
              <CardTitle>
                <Star size={20} />
                Recent Shows
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="top-shows-list">
                {stats.content.most_reviewed_shows.map((show, index) => (
                  <div key={show.title} className="top-show-item">
                    <div className="show-rank">#{index + 1}</div>
                    {(show.backdrop_url || show.image_url) && (
                      <img 
                        src={show.backdrop_url || show.image_url} 
                        alt={show.title} 
                        className="show-image"
                      />
                    )}
                    <div className="show-info">
                      <div className="show-title">{show.title}</div>
                      <div className="show-stats">
                        {show.review_count} reviews • ⭐ {show.avg_rating}/10
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Database Performance */}
        <Card className="migration-card">
          <CardHeader>
            <CardTitle>
              <Database size={20} />
              Database Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="migration-description">
              Create database indexes to improve query performance and support more concurrent users.
              This is safe to run multiple times - existing indexes will be skipped.
            </p>
            
            <div className="capacity-info">
              <div className="capacity-row">
                <span className="capacity-label">Without Indexes:</span>
                <span className="capacity-value warning">~500-1,000 users</span>
              </div>
              <div className="capacity-row">
                <span className="capacity-label">With Indexes:</span>
                <span className="capacity-value good">~5,000-10,000 users</span>
              </div>
            </div>

            {indexStatus && (
              <div className="index-status-display">
                <div className="status-header">
                  <span className="status-icon">{indexStatus.is_optimized ? '✅' : '⚠️'}</span>
                  <span className="status-text">
                    {indexStatus.is_optimized ? 'Database Optimized' : 'Optimization Needed'}
                  </span>
                </div>
                <div className="index-stats">
                  <div className="index-stat">
                    <span className="stat-number">{indexStatus.total_indexes}</span>
                    <span className="stat-label">Current Indexes</span>
                  </div>
                  <div className="index-stat">
                    <span className="stat-number">{indexStatus.expected_indexes}</span>
                    <span className="stat-label">Expected Indexes</span>
                  </div>
                  <div className="index-stat">
                    <span className={`stat-number ${indexStatus.is_optimized ? 'good' : 'warning'}`}>
                      {indexStatus.coverage_percent}%
                    </span>
                    <span className="stat-label">Coverage</span>
                  </div>
                </div>
              </div>
            )}

            <div className="migration-actions">
              <Button 
                onClick={createDatabaseIndexes} 
                disabled={creatingIndexes}
                className="migrate-button"
              >
                {creatingIndexes ? (
                  <>
                    <Loader2 size={16} className="spinning" />
                    Creating Indexes...
                  </>
                ) : (
                  <>
                    <Database size={16} />
                    {indexStatus?.is_optimized ? 'Refresh Indexes' : 'Create Indexes'}
                  </>
                )}
              </Button>
            </div>

            {indexStatus?.is_optimized && (
              <p className="migration-complete">✅ All indexes exist! Database is optimized for up to 10,000 users.</p>
            )}
          </CardContent>
        </Card>

        {/* Synopsis Backfill Card */}
        <Card className="scheduler-card">
          <CardHeader>
            <CardTitle className="scheduler-title">
              <Film size={20} />
              Synopsis Backfill Tool
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="tool-description" style={{ marginBottom: '1rem', color: '#999' }}>
              Fetch and add TMDB synopses to existing shows that are missing them.
            </p>
            
            {synopsisStats && (
              <div className="index-stats" style={{ marginBottom: '1rem' }}>
                <div className="index-stat">
                  <span className="stat-number">{synopsisStats.total_shows}</span>
                  <span className="stat-label">Total Shows</span>
                </div>
                <div className="index-stat">
                  <span className="stat-number good">{synopsisStats.with_synopsis}</span>
                  <span className="stat-label">With Synopsis</span>
                </div>
                <div className="index-stat">
                  <span className="stat-number warning">{synopsisStats.can_backfill}</span>
                  <span className="stat-label">Can Backfill</span>
                </div>
              </div>
            )}

            <div className="migration-actions">
              <Button 
                onClick={runSynopsisBackfill} 
                disabled={backfillingSync || (synopsisStats?.can_backfill === 0)}
                className="migrate-button"
              >
                {backfillingSync ? (
                  <>
                    <Loader2 size={16} className="spinning" />
                    Backfilling...
                  </>
                ) : (
                  <>
                    <Film size={16} />
                    Backfill Synopses ({synopsisStats?.can_backfill || 0})
                  </>
                )}
              </Button>
            </div>

            {synopsisStats?.can_backfill === 0 && synopsisStats?.total_shows > 0 && (
              <p className="migration-complete">✅ All shows have synopses!</p>
            )}
          </CardContent>
        </Card>

        {/* Streaming Services Card */}
        <AdminStreamingServices />

        {/* Scheduler Settings Card */}
        <Card className="scheduler-card">
          <CardHeader>
            <CardTitle className="scheduler-title">
              <Clock size={20} />
              Account Deletion Scheduler
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="scheduler-content">
              <div className="scheduler-status">
                <div className="status-row">
                  <span className="status-label">Status:</span>
                  <span className={`status-value ${schedulerSettings?.enabled ? 'enabled' : 'disabled'}`}>
                    {schedulerSettings?.enabled ? '🟢 Enabled' : '🔴 Disabled'}
                  </span>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={toggleScheduler}
                    disabled={updatingScheduler}
                  >
                    {schedulerSettings?.enabled ? 'Disable' : 'Enable'}
                  </Button>
                </div>
                <div className="status-row">
                  <span className="status-label">Runs Every:</span>
                  <select 
                    className="interval-select"
                    value={schedulerSettings?.interval_days || 30}
                    onChange={(e) => updateSchedulerInterval(parseInt(e.target.value))}
                    disabled={updatingScheduler}
                  >
                    <option value="1">Daily</option>
                    <option value="7">Weekly</option>
                    <option value="14">Every 2 Weeks</option>
                    <option value="30">Monthly</option>
                    <option value="60">Every 2 Months</option>
                    <option value="90">Quarterly</option>
                  </select>
                </div>
                {schedulerSettings?.next_run && (
                  <div className="status-row">
                    <span className="status-label">Next Run:</span>
                    <span className="status-value">
                      {new Date(schedulerSettings.next_run).toLocaleString()}
                    </span>
                  </div>
                )}
                {schedulerSettings?.last_run && (
                  <div className="status-row">
                    <span className="status-label">Last Run:</span>
                    <span className="status-value">
                      {new Date(schedulerSettings.last_run).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>

              <div className="pending-deletions-section">
                <h4>Pending Deletions: {pendingDeletions?.count || 0}</h4>
                {pendingDeletions?.pending_deletions?.length > 0 ? (
                  <div className="pending-list">
                    {pendingDeletions.pending_deletions.map((p, idx) => (
                      <div key={idx} className="pending-item">
                        <span className="pending-user">{p.user_email || p.username || p.user_id}</span>
                        <span className="pending-date">
                          Deletes: {new Date(p.scheduled_deletion_date).toLocaleDateString()}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-pending">No accounts pending deletion</p>
                )}
              </div>

              <Button 
                onClick={runDeletionProcessNow}
                disabled={runningDeletionNow || !pendingDeletions?.count}
                className="run-now-button"
                variant="outline"
              >
                {runningDeletionNow ? (
                  <>
                    <Loader2 size={16} className="spinning" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Play size={16} />
                    Run Deletion Process Now
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Email Management Link */}
        <Link to="/admin/email" className="admin-link-card" data-testid="email-admin-link">
          <Card className="link-card">
            <CardContent className="link-card-content">
              <div className="link-card-icon">
                <Mail size={28} />
              </div>
              <div className="link-card-info">
                <h3>Email Management</h3>
                <p>Send announcements, manage recipients, and view analytics</p>
              </div>
              <ChevronRight size={24} className="link-card-arrow" />
            </CardContent>
          </Card>
        </Link>
      </div>

      <style jsx>{`
        .admin-page {
          min-height: 100vh;
          background: #121212;
          color: #ffffff;
        }

        .admin-content {
          max-width: 1400px;
          margin: 0 auto;
          padding: 2rem;
        }

        .page-header {
          margin-bottom: 2.5rem;
        }

        .page-header h1 {
          font-size: 2.5rem;
          color: #E62F88;
          margin-bottom: 0.5rem;
        }

        .page-header p {
          color: #b0b0b0;
          font-size: 1.1rem;
        }

        /* Admin Link Card */
        .admin-link-card {
          display: block;
          text-decoration: none;
          margin-bottom: 2rem;
        }

        :global(.link-card) {
          background: #1a1a1a !important;
          border: 1px solid #333 !important;
          transition: all 0.2s ease;
        }

        :global(.link-card:hover) {
          border-color: #E62F88 !important;
          transform: translateY(-2px);
        }

        :global(.link-card-content) {
          display: flex;
          align-items: center;
          gap: 1.5rem;
          padding: 1.5rem !important;
        }

        .link-card-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 56px;
          height: 56px;
          background: rgba(230, 47, 136, 0.15);
          border-radius: 12px;
          color: #E62F88;
          flex-shrink: 0;
        }

        .link-card-info {
          flex: 1;
        }

        .link-card-info h3 {
          color: #ffffff;
          font-size: 1.25rem;
          font-weight: 600;
          margin-bottom: 0.25rem;
        }

        .link-card-info p {
          color: #888;
          font-size: 0.9rem;
          margin: 0;
        }

        :global(.link-card-arrow) {
          color: #666;
          flex-shrink: 0;
        }

        :global(.link-card:hover .link-card-arrow) {
          color: #E62F88;
        }

        .metrics-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .metric-card {
          background: #1a1a1a;
          border: 1px solid #333333;
        }

        .metric-title {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          color: #E62F88;
          font-size: 1rem;
        }

        .metric-value {
          font-size: 3rem;
          font-weight: 700;
          color: #E62F88;
          margin: 1rem 0;
        }

        .metric-subtitle {
          color: #b0b0b0;
          font-size: 0.9rem;
        }

        .metric-details {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          color: #b0b0b0;
          font-size: 0.875rem;
        }

        .metric-secondary {
          color: #ffffff;
          margin-top: 0.5rem;
          font-size: 1.1rem;
        }

        .adsense-card {
          background: #1a1a1a;
          border: 2px solid #333333;
          margin-bottom: 2rem;
        }

        .adsense-card.ready {
          border-color: #4caf50;
        }

        .adsense-title {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          color: #E62F88;
        }

        .adsense-status {
          display: flex;
          align-items: center;
          gap: 1.5rem;
          margin-bottom: 1.5rem;
        }

        .status-icon {
          font-size: 3rem;
        }

        .status-text h3 {
          font-size: 1.5rem;
          color: #ffffff;
          margin-bottom: 0.5rem;
        }

        .status-text p {
          color: #b0b0b0;
          line-height: 1.5;
        }

        .adsense-requirements {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          padding: 1rem;
          background: #2a2a2a;
          border-radius: 8px;
        }

        .requirement {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          color: #ffffff;
        }

        .requirement .met {
          color: #4caf50;
          font-size: 1.2rem;
        }

        .requirement .unmet {
          color: #666666;
          font-size: 1.2rem;
        }

        .chart-card {
          background: #1a1a1a;
          border: 1px solid #333333;
          margin-bottom: 2rem;
        }

        .period-selector {
          display: flex;
          gap: 0.5rem;
          margin-top: 1rem;
        }

        .period-selector button {
          padding: 0.5rem 1rem;
          background: #2a2a2a;
          color: #ffffff;
          border: 1px solid #333333;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.875rem;
          transition: all 0.2s;
        }

        .period-selector button:hover {
          background: #333333;
        }

        .period-selector button.active {
          background: #E62F88;
          color: #000000;
          border-color: #E62F88;
        }

        .simple-chart {
          display: flex;
          align-items: flex-end;
          gap: 4px;
          height: 200px;
          padding: 1rem 0;
        }

        .chart-bar-container {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          height: 100%;
        }

        .chart-bar {
          width: 100%;
          background: linear-gradient(to top, #E62F88, #ffd54f);
          border-radius: 4px 4px 0 0;
          min-height: 2px;
          display: flex;
          align-items: flex-start;
          justify-content: center;
          padding-top: 4px;
          transition: all 0.3s;
        }

        .chart-bar:hover {
          opacity: 0.8;
        }

        .bar-value {
          font-size: 0.7rem;
          color: #000000;
          font-weight: 600;
        }

        .chart-label {
          font-size: 0.7rem;
          color: #b0b0b0;
          margin-top: 0.5rem;
        }

        .breakdown-card {
          background: #1a1a1a;
          border: 1px solid #333333;
          margin-bottom: 2rem;
        }

        .breakdown-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .breakdown-item {
          padding: 0.75rem;
          background: #2a2a2a;
          border-radius: 8px;
        }

        .breakdown-bar-container {
          display: grid;
          grid-template-columns: 150px 1fr 60px;
          align-items: center;
          gap: 1rem;
        }

        .breakdown-label {
          color: #ffffff;
          font-weight: 500;
        }

        .breakdown-bar-wrapper {
          background: #1a1a1a;
          height: 24px;
          border-radius: 4px;
          overflow: hidden;
        }

        .breakdown-bar {
          height: 100%;
          background: #E62F88;
          transition: width 0.3s;
        }

        .breakdown-count {
          color: #E62F88;
          font-weight: 600;
          text-align: right;
        }

        .traffic-stats {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .traffic-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem;
          background: #2a2a2a;
          border-radius: 8px;
        }

        .traffic-row.landing-row {
          background: linear-gradient(135deg, #2a2a2a 0%, #1a1a2e 100%);
          border: 1px solid #E62F88;
        }

        .traffic-label {
          color: #ffffff;
          font-weight: 600;
          font-size: 1rem;
        }

        .traffic-values {
          display: flex;
          gap: 1.5rem;
        }

        .human-count {
          color: #22c55e;
          font-weight: 600;
        }

        .bot-count {
          color: #f59e0b;
          font-weight: 600;
        }

        .chart-controls {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .page-selector {
          display: flex;
          gap: 0.5rem;
        }

        .page-selector button {
          padding: 0.25rem 0.75rem;
          font-size: 0.8rem;
          border: 1px solid #444;
          background: transparent;
          color: #b0b0b0;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .page-selector button:hover {
          border-color: #E62F88;
          color: #ffffff;
        }

        .page-selector button.active {
          background: #E62F88;
          color: #000000;
          border-color: #E62F88;
        }

        .traffic-legend {
          display: flex;
          justify-content: center;
          gap: 2rem;
          margin-bottom: 1rem;
          padding: 0.5rem;
          background: #2a2a2a;
          border-radius: 4px;
        }

        .legend-item {
          font-size: 0.85rem;
          font-weight: 500;
        }

        .legend-item.human {
          color: #22c55e;
        }

        .legend-item.bot {
          color: #f59e0b;
        }

        .stacked-chart {
          display: flex;
          align-items: flex-end;
          gap: 4px;
          height: 200px;
          padding: 1rem 0;
        }

        .stacked-bar-container {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          height: 100%;
        }

        .stacked-bar {
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column-reverse;
          border-radius: 4px 4px 0 0;
          overflow: hidden;
        }

        .bar-segment {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s;
          min-height: 2px;
        }

        .bar-segment.human {
          background: #22c55e;
        }

        .bar-segment.bot {
          background: #f59e0b;
        }

        .segment-value {
          font-size: 0.65rem;
          color: #000000;
          font-weight: 600;
        }

        .top-shows-card {
          background: #1a1a1a;
          border: 1px solid #333333;
        }

        .top-shows-card :global(h3) {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #E62F88;
        }

        .top-shows-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .top-show-item {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          background: #2a2a2a;
          border-radius: 8px;
        }

        .show-rank {
          font-size: 1.5rem;
          font-weight: 700;
          color: #E62F88;
          min-width: 50px;
          text-align: center;
        }

        .show-image {
          width: 120px;
          height: 68px;
          object-fit: cover;
          border-radius: 6px;
          flex-shrink: 0;
        }

        .show-info {
          flex: 1;
        }

        .show-title {
          font-size: 1.1rem;
          color: #ffffff;
          margin-bottom: 0.25rem;
        }

        .show-stats {
          color: #b0b0b0;
          font-size: 0.875rem;
        }

        .chart-note {
          text-align: center;
          color: #b0b0b0;
          font-size: 0.75rem;
          margin-top: 0.5rem;
          font-style: italic;
        }

        /* Migration Card Styles */
        .migration-card {
          background: #1a1a1a;
          border: 1px solid #333333;
          margin-bottom: 2rem;
        }

        .migration-card :global(.card-title) {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          color: #E62F88;
        }

        .migration-description {
          color: #b0b0b0;
          margin-bottom: 1.5rem;
          line-height: 1.5;
        }

        .capacity-info {
          margin-bottom: 1.5rem;
        }

        .capacity-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem 0;
          border-bottom: 1px solid #333333;
        }

        .capacity-row:last-child {
          border-bottom: none;
        }

        .capacity-label {
          color: #b0b0b0;
          font-size: 0.9rem;
        }

        .capacity-value {
          font-weight: 600;
          font-size: 1rem;
        }

        .capacity-value.good {
          color: #4caf50;
        }

        .capacity-value.warning {
          color: #f59e0b;
        }

        .index-status-display {
          background: #2a2a2a;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1.5rem;
        }

        .status-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 1rem;
          padding-bottom: 0.75rem;
          border-bottom: 1px solid #333333;
        }

        .status-icon {
          font-size: 1.25rem;
        }

        .status-text {
          font-size: 1rem;
          font-weight: 600;
          color: #ffffff;
        }

        .index-stats {
          display: flex;
          justify-content: space-around;
          gap: 1rem;
        }

        .index-stat {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }

        .stat-number {
          font-size: 1.75rem;
          font-weight: 700;
          color: #ffffff;
        }

        .stat-number.good {
          color: #4caf50;
        }

        .stat-number.warning {
          color: #f59e0b;
        }

        .index-stat .stat-label {
          font-size: 0.75rem;
          color: #888888;
          margin-top: 0.25rem;
        }

        .migration-stats {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1rem;
          margin-bottom: 1.5rem;
          padding: 1rem;
          background: #2a2a2a;
          border-radius: 8px;
        }

        .migration-stat {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }

        .stat-label {
          font-size: 0.75rem;
          color: #888888;
          margin-bottom: 0.25rem;
        }

        .stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: #ffffff;
        }

        .stat-value.good {
          color: #4caf50;
        }

        .stat-value.warning {
          color: #f59e0b;
        }

        .migration-shows-list {
          margin-bottom: 1.5rem;
        }

        .list-label {
          font-size: 0.875rem;
          color: #888888;
          margin-bottom: 0.5rem;
        }

        .shows-chips {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .show-chip {
          font-size: 0.75rem;
          padding: 0.25rem 0.75rem;
          background: #333333;
          color: #ffffff;
          border-radius: 20px;
        }

        .show-chip.more {
          background: #E62F88;
          color: #000000;
        }

        .migration-actions {
          display: flex;
          gap: 1rem;
          margin-top: 1rem;
        }

        .migration-actions button {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .migrate-button {
          background: #E62F88 !important;
          color: #000000 !important;
        }

        .migrate-button:disabled {
          background: #666666 !important;
          color: #999999 !important;
        }

        .spinning {
          animation: spin 1s linear infinite;
        }

        .migration-complete {
          margin-top: 1rem;
          color: #4caf50;
          font-weight: 600;
        }

        /* Scheduler Card Styles */
        .scheduler-card {
          background: #1a1a1a !important;
          border: 1px solid #333 !important;
          margin-bottom: 2rem;
        }

        .scheduler-title {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #E62F88 !important;
        }

        .scheduler-content {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .scheduler-status {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .status-row {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .status-label {
          color: #b0b0b0;
          min-width: 100px;
        }

        .status-value {
          color: #ffffff;
        }

        .status-value.enabled {
          color: #4ade80;
        }

        .status-value.disabled {
          color: #ef4444;
        }

        .interval-select {
          background: #2a2a2a;
          border: 1px solid #444;
          color: #ffffff;
          padding: 0.5rem 1rem;
          border-radius: 6px;
          cursor: pointer;
        }

        .interval-select:hover {
          border-color: #E62F88;
        }

        .pending-deletions-section h4 {
          color: #ffffff;
          margin-bottom: 0.75rem;
        }

        .pending-list {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          max-height: 200px;
          overflow-y: auto;
        }

        .pending-item {
          display: flex;
          justify-content: space-between;
          padding: 0.5rem 0.75rem;
          background: #2a2a2a;
          border-radius: 6px;
        }

        .pending-user {
          color: #ffffff;
        }

        .pending-date {
          color: #f59e0b;
          font-size: 0.85rem;
        }

        .no-pending {
          color: #666;
          font-style: italic;
        }

        .run-now-button {
          align-self: flex-start;
        }

        @media (max-width: 768px) {
          .admin-content {
            padding: 1rem;
            padding-bottom: 100px;
          }

          .page-header h1 {
            font-size: 2rem;
          }

          .metrics-grid {
            grid-template-columns: 1fr;
          }

          .metric-value {
            font-size: 2.5rem;
          }

          .adsense-status {
            flex-direction: column;
            align-items: flex-start;
          }

          .breakdown-bar-container {
            grid-template-columns: 100px 1fr 50px;
            gap: 0.5rem;
          }

          .breakdown-label {
            font-size: 0.875rem;
          }

          .simple-chart {
            gap: 8px;
          }

          .chart-bar-container {
            min-width: 40px;
          }

          .chart-bar {
            min-width: 30px;
          }

          .chart-label {
            display: block;
            font-size: 0.65rem;
            white-space: nowrap;
          }

          .bar-value {
            font-size: 0.75rem;
          }

          .migration-stats {
            grid-template-columns: repeat(2, 1fr);
          }

          .migration-actions {
            flex-direction: column;
          }

          .migration-actions button {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};

export default Admin;
